import { useMemo, useRef, useState } from 'react';
import { niceTicks } from './StackedColumns';
import { TooltipLayer, TooltipRow, useTooltip } from './Tooltip';
import { useMeasure } from './useMeasure';

export interface LineSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

export interface LineChartProps {
  categories: string[];
  series: LineSeries[];
  height?: number;
  emptyText?: string;
}

const MARKER_RADIUS = 4.5;
/** Över fyra serier bär legenden identiteten och etikettmarginalen behövs inte. */
const MAX_DIRECT_LABELS = 4;

/**
 * Linjediagram med hårkors och samlad tooltip. Upp till fyra serier får
 * direktetikett vid sista punkten – utöver det bär legenden identiteten.
 */
export function LineChart({ categories, series, height = 300, emptyText }: LineChartProps) {
  const { tooltip, show, hide } = useTooltip();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [containerRef, containerWidth] = useMeasure<HTMLDivElement>(720);
  const svgRef = useRef<SVGSVGElement>(null);

  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const ticks = useMemo(() => niceTicks(max, 4), [max]);
  const axisMax = ticks[ticks.length - 1];

  if (series.length === 0 || categories.length === 0) {
    return <p className="empty">{emptyText ?? 'Välj minst en teknik för att se utvecklingen över tid.'}</p>;
  }

  const showDirectLabels = series.length <= MAX_DIRECT_LABELS;
  const MARGIN = { top: 18, right: showDirectLabels ? 84 : 16, bottom: 32, left: 42 };

  const width = Math.max(420, containerWidth);
  const plotWidth = width - MARGIN.left - MARGIN.right;
  const plotHeight = height - MARGIN.top - MARGIN.bottom;
  const stepX = categories.length > 1 ? plotWidth / (categories.length - 1) : 0;

  const xOf = (index: number) => MARGIN.left + stepX * index;
  const yOf = (value: number) => MARGIN.top + plotHeight - (value / axisMax) * plotHeight;

  const handleMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const relative = ((event.clientX - rect.left) / rect.width) * width;
    const index = Math.round((relative - MARGIN.left) / (stepX || 1));
    if (index < 0 || index >= categories.length) {
      setHoverIndex(null);
      hide();
      return;
    }
    setHoverIndex(index);
    show(
      event,
      <>
        <div className="tooltip__title">{categories[index]}</div>
        {series
          .map((s) => ({ s, value: s.values[index] ?? 0 }))
          .sort((a, b) => b.value - a.value)
          .map(({ s, value }) => (
            <TooltipRow key={s.key} color={s.color} label={s.label} value={value} />
          ))}
      </>,
    );
  };

  return (
    <>
      <div className="chart-scroll" ref={containerRef}>
        <svg
          ref={svgRef}
          className="chart"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMinYMin meet"
          role="img"
          aria-label={`Utveckling över tid för ${series.map((s) => s.label).join(', ')}`}
          onMouseMove={handleMove}
          onMouseLeave={() => {
            setHoverIndex(null);
            hide();
          }}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line className="chart__grid" x1={MARGIN.left} x2={width - MARGIN.right} y1={yOf(tick)} y2={yOf(tick)} />
              <text className="chart__tick" x={MARGIN.left - 8} y={yOf(tick) + 4} textAnchor="end">
                {tick}
              </text>
            </g>
          ))}

          {hoverIndex !== null ? (
            <line
              className="chart__crosshair"
              x1={xOf(hoverIndex)}
              x2={xOf(hoverIndex)}
              y1={MARGIN.top}
              y2={MARGIN.top + plotHeight}
            />
          ) : null}

          {series.map((s) => {
            const path = s.values
              .map((value, index) => `${index === 0 ? 'M' : 'L'} ${xOf(index)} ${yOf(value)}`)
              .join(' ');
            const lastIndex = s.values.length - 1;

            return (
              <g key={s.key}>
                <path d={path} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                {hoverIndex !== null ? (
                  <circle
                    cx={xOf(hoverIndex)}
                    cy={yOf(s.values[hoverIndex] ?? 0)}
                    r={MARKER_RADIUS}
                    fill={s.color}
                    stroke="var(--surface-1)"
                    strokeWidth={2}
                  />
                ) : null}
                {showDirectLabels ? (
                  <text
                    className="chart__label"
                    x={xOf(lastIndex) + 10}
                    y={yOf(s.values[lastIndex] ?? 0) + 4}
                    fill="var(--text-secondary)"
                  >
                    {s.label.length > 12 ? `${s.label.slice(0, 11)}…` : s.label}
                  </text>
                ) : null}
              </g>
            );
          })}

          <line
            className="chart__axis"
            x1={MARGIN.left}
            x2={width - MARGIN.right}
            y1={MARGIN.top + plotHeight}
            y2={MARGIN.top + plotHeight}
          />

          {categories.map((category, index) => (
            <text
              key={category}
              className="chart__tick"
              x={xOf(index)}
              y={height - MARGIN.bottom + 18}
              textAnchor="middle"
            >
              {category}
            </text>
          ))}
        </svg>
      </div>
      <TooltipLayer tooltip={tooltip} />
    </>
  );
}
